import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import groupBy from 'lodash/groupBy';
import uniq from 'lodash/uniq';
import {
  Accordion,
  AccordionTab,
  AccordionTabChangeEvent,
} from 'primereact/accordion';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '@/app/hooks';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { ConfirmProvider } from '@/components/ui/confirm-dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useEditEmbryos } from '@/services/embryo-service';
import { useEditSemen } from '@/services/semen-service/hooks';
import {
  useShipmentCalves,
  useShipmentCaneSuggestion,
  useShipmentDetail,
  useShipmentDonors,
  useShipmentEmbryos,
  useShipmentRecipients,
  useShipmentSemen,
} from '@/services/shipping/hooks';
import type { ShipmentEmbryo, ShipmentSemen } from '@/services/shipping/types';

import { countRecaneGroups } from '../shipping/utils/countRecaneGroups';
import {
  BulkEditShipmentItemsDialog,
  type BulkEditShipmentItemsUpdates,
  type BulkEditShipmentItemType,
} from './components/BulkEditShipmentItemsDialog';
import { EmbryosMenubar } from './components/EmbryosMenubar';
import { MultipleCanesBanner } from './components/MultipleCanesBanner';
import { RecaneBanner } from './components/RecaneBanner';
import { SemenMenubar } from './components/SemenMenubar';
import { type ShipmentEmbryoGridHandle } from './components/ShipmentEmbryoGrid';
import { ShipmentNotifications } from './components/ShipmentNotifications';
import { type ShipmentSemenGridHandle } from './components/ShipmentSemenGrid';
import { useRemoveFromShipmentWorkflow } from './hooks/useRemoveFromShipmentWorkflow';
import { useRemoveSemenFromShipmentWorkflow } from './hooks/useRemoveSemenFromShipmentWorkflow';
import { Shipment } from './Shipment';

import './Shipment.css';

export function ShipmentPage() {
  return (
    <ConfirmProvider>
      <ShipmentDetails />
    </ConfirmProvider>
  );
}

const ShipmentDetails = () => {
  const [params] = useSearchParams();
  const id = params.get('id');
  const [activeIndexes, setActiveIndexes] = useState<number[]>([]);

  const { data: shipment, refetch: refetchShipment } = useShipmentDetail(id);
  const { data: embryos, refetch: refetchEmbryos } = useShipmentEmbryos(id);
  const { data: semen, refetch: refetchSemen } = useShipmentSemen(id);
  const { data: donors } = useShipmentDonors(id);
  const { data: recipients } = useShipmentRecipients(id);
  const { data: calves } = useShipmentCalves(id);
  const isCaneSuggestionEnabled = useFeatureFlag(
    'Recane_Unique_Cane_Suggestion',
  );
  const isShippingMassEditEnabled = useFeatureFlag('Shipping_Bulk_Edit_Items');
  const bulkEmbryoSiteEnabled = useFeatureFlag('Shipping_Bulk_Embryo_Site');
  const isRemoveSemenFromShipmentEnabled = useFeatureFlag(
    'Shipping_Add_Option_To_Remove_Semen_From_Shipment',
  );
  const { data: shipmentCaneSuggestion } = useShipmentCaneSuggestion(
    id,
    isCaneSuggestionEnabled,
  );

  const recaneEmbryoIds = useMemo(() => {
    return shipment?.recaneEmbryos?.map((embryo) => embryo.id);
  }, [shipment?.recaneEmbryos]);

  const recaneEmbryoCount = useMemo(() => {
    if (!isCaneSuggestionEnabled) return undefined;
    return countRecaneGroups(shipment?.recaneEmbryos);
  }, [isCaneSuggestionEnabled, shipment?.recaneEmbryos]);

  const multipleCanesEmbryoIds = useMemo(() => {
    if (!embryos) return undefined;

    const embryosWithMultipleCanesOrFreezeDates: string[] = [];
    const matingGroups = Object.entries(groupBy(embryos, 'name')).filter(
      ([_, group]) =>
        uniq(group.map((x) => x.cane)).length > 1 ||
        uniq(group.map((x) => x.creation)).length > 1,
    );

    for (const [_, group] of matingGroups) {
      embryosWithMultipleCanesOrFreezeDates.push(...group.map((e) => e.id));
    }

    return embryosWithMultipleCanesOrFreezeDates;
  }, [embryos]);

  useEffect(() => {
    const newActiveIndexes = [];
    if (embryos && embryos.length > 0) newActiveIndexes.push(0);
    if (semen && semen.length > 0) newActiveIndexes.push(1);
    if (donors && donors.length > 0) newActiveIndexes.push(2);
    if (recipients && recipients.length > 0) newActiveIndexes.push(3);
    if (calves && calves.length > 0) newActiveIndexes.push(4);
    setActiveIndexes(newActiveIndexes);
  }, [embryos, calves, semen, donors, recipients]);

  const handleTabChange = (e: AccordionTabChangeEvent) => {
    setActiveIndexes(Array.isArray(e.index) ? e.index : [e.index]);
  };

  const [selectedEmbryos, setSelectedEmbryos] = useState<ShipmentEmbryo[]>([]);
  const [selectedSemen, setSelectedSemen] = useState<ShipmentSemen[]>([]);
  const [bulkEditItemType, setBulkEditItemType] =
    useState<BulkEditShipmentItemType | null>(null);

  const embryoGridRef = useRef<ShipmentEmbryoGridHandle>(null);
  const semenGridRef = useRef<ShipmentSemenGridHandle>(null);
  const dispatch = useAppDispatch();
  const { mutateAsync: editEmbryosMutation, isPending: isEditingEmbryos } =
    useEditEmbryos();
  const { mutateAsync: editSemenMutation, isPending: isEditingSemen } =
    useEditSemen();

  const handleSelectedEmbryosChange = useCallback(
    (nextSelectedEmbryos: ShipmentEmbryo[]) => {
      setSelectedEmbryos(nextSelectedEmbryos);
      if (isShippingMassEditEnabled && nextSelectedEmbryos.length > 0) {
        semenGridRef.current?.deselectAll();
      }
    },
    [isShippingMassEditEnabled],
  );

  const handleSelectedSemenChange = useCallback(
    (nextSelectedSemen: ShipmentSemen[]) => {
      setSelectedSemen(nextSelectedSemen);
      if (isShippingMassEditEnabled && nextSelectedSemen.length > 0) {
        embryoGridRef.current?.deselectAll();
      }
    },
    [isShippingMassEditEnabled],
  );

  const canExecuteWorkflow = selectedEmbryos.length > 0;

  const handleBulkEditSubmit = useCallback(
    async (updates: BulkEditShipmentItemsUpdates) => {
      try {
        if (bulkEditItemType === 'embryo') {
          await editEmbryosMutation(
            selectedEmbryos.map((embryo) => ({
              id: embryo.id,
              embryoId: embryo.id,
              ...(updates.status !== undefined
                ? { status: updates.status }
                : {}),
              ...(updates.storageLocationId
                ? {
                    storageLocationId: updates.storageLocationId,
                    ...(bulkEmbryoSiteEnabled
                      ? { siteId: updates.siteId }
                      : {}),
                  }
                : {}),
            })),
          );

          await refetchEmbryos();
          embryoGridRef.current?.deselectAll();
        }

        if (bulkEditItemType === 'semen') {
          await editSemenMutation(
            selectedSemen.map((semenItem) => ({
              semenId: semenItem.id,
              ...(id ? { shipmentId: id } : {}),
              ...(updates.storageLocationId
                ? {
                    storageLocationId: updates.storageLocationId,
                    storageLocationName: updates.storageLocationName ?? '',
                  }
                : {}),
            })),
          );

          await refetchSemen();
          semenGridRef.current?.deselectAll();
        }

        await refetchShipment();
        setBulkEditItemType(null);
        dispatch(
          addToast({
            severity: 'success',
            summary: 'Success',
            detail: 'Successfully updated selected shipment items',
            life: ToastLife.MEDIUM,
          }),
        );
      } catch (_error) {
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update selected shipment items',
            life: ToastLife.MEDIUM,
          }),
        );
      }
    },
    [
      bulkEditItemType,
      dispatch,
      editEmbryosMutation,
      editSemenMutation,
      id,
      refetchEmbryos,
      refetchSemen,
      refetchShipment,
      selectedEmbryos,
      selectedSemen,
    ],
  );

  const { executeWorkflow: executeRemoveFromShipmentWorkflow } =
    useRemoveFromShipmentWorkflow({
      dispatch,
      onSuccess: () => {
        embryoGridRef.current?.deselectAll();
      },
    });

  const { executeWorkflow: executeRemoveSemenFromShipmentWorkflow } =
    useRemoveSemenFromShipmentWorkflow({
      shipmentId: id,
      dispatch,
      onSuccess: () => {
        semenGridRef.current?.deselectAll();
        startTransition(() => {
          refetchSemen();
          refetchShipment();
        });
      },
    });

  return (
    <Shipment
      className="shipping-page"
      shipment={shipment}
      embryos={embryos}
      semen={semen}
      donors={donors}
      recipients={recipients}
      calves={calves}
    >
      <ShipmentNotifications />

      <RecaneBanner recaneEmbryoCount={recaneEmbryoCount} />

      {multipleCanesEmbryoIds && multipleCanesEmbryoIds.length > 0 && (
        <MultipleCanesBanner />
      )}

      {isShippingMassEditEnabled && bulkEditItemType && (
        <BulkEditShipmentItemsDialog
          visible={Boolean(bulkEditItemType)}
          itemType={bulkEditItemType}
          selectedItems={
            bulkEditItemType === 'embryo' ? selectedEmbryos : selectedSemen
          }
          onHide={() => setBulkEditItemType(null)}
          onSubmit={handleBulkEditSubmit}
          isSubmitting={isEditingEmbryos || isEditingSemen}
          allowStatusUpdate={bulkEditItemType === 'embryo'}
          allowStorageLocationUpdate
        />
      )}

      <Shipment.Form />

      <Accordion
        multiple
        activeIndex={activeIndexes}
        onTabChange={handleTabChange}
      >
        <AccordionTab header="Embryos" disabled={!embryos?.length}>
          <div className="mb-4 flex justify-end">
            <EmbryosMenubar
              selectedEmbryos={selectedEmbryos}
              occupiedCaneCodes={
                shipmentCaneSuggestion?.occupiedCaneCodes ?? []
              }
              isBulkEditEnabled={isShippingMassEditEnabled}
              openBulkEditDialog={() => {
                if (selectedEmbryos.length === 1) {
                  embryoGridRef.current?.openEditDialog();
                } else {
                  setBulkEditItemType('embryo');
                }
              }}
              refetchEmbryos={refetchEmbryos}
              refetchShipment={refetchShipment}
              executeRemoveFromShipmentWorkflow={
                executeRemoveFromShipmentWorkflow
              }
              canExecuteWorkflow={canExecuteWorkflow}
            />
          </div>
          <Shipment.EmbryoGrid
            ref={embryoGridRef}
            embryos={embryos}
            selectedEmbryos={selectedEmbryos}
            setSelectedEmbryos={
              isShippingMassEditEnabled
                ? handleSelectedEmbryosChange
                : setSelectedEmbryos
            }
            refetchEmbryos={refetchEmbryos}
            recaneEmbryoIds={recaneEmbryoIds}
            multipleCanesEmbryoIds={multipleCanesEmbryoIds}
            onRemoveFromShipment={executeRemoveFromShipmentWorkflow}
            canExecuteWorkflow={canExecuteWorkflow}
          />
        </AccordionTab>
        <AccordionTab header="Semen" disabled={!semen?.length}>
          {(isShippingMassEditEnabled || isRemoveSemenFromShipmentEnabled) && (
            <div className="mb-4 flex justify-end">
              <SemenMenubar
                selectedSemen={selectedSemen}
                isBulkEditEnabled={isShippingMassEditEnabled}
                openBulkEditDialog={() => setBulkEditItemType('semen')}
                executeRemoveFromShipmentWorkflow={
                  executeRemoveSemenFromShipmentWorkflow
                }
              />
            </div>
          )}
          <Shipment.SemenGrid
            ref={semenGridRef}
            semen={semen}
            selectedSemen={selectedSemen}
            setSelectedSemen={handleSelectedSemenChange}
            enableSelection={
              isShippingMassEditEnabled || isRemoveSemenFromShipmentEnabled
            }
          />
        </AccordionTab>
        <AccordionTab header="Donors" disabled={!donors?.length}>
          <Shipment.DonorsGrid donors={donors} />
        </AccordionTab>
        <AccordionTab header="Recipients" disabled={!recipients?.length}>
          <Shipment.RecipientGrid recipients={recipients} />
        </AccordionTab>
        <AccordionTab header="Calves" disabled={!calves?.length}>
          <Shipment.CalvesGrid calves={calves} />
        </AccordionTab>
        <AccordionTab header="Additional Data">
          <Shipment.AdditionalData />
        </AccordionTab>
      </Accordion>
    </Shipment>
  );
};
